import * as api from '../../requests'
import {BUSINESS_RULES} from './businessRules.js'
import store, {resolveFieldEngine, resolveFieldLogic} from '../../store.js'
import {getUploadsFromFields} from './fieldComponents/UploadAccumulator'
import { getCurrentWizardAmendmentNum, testRepeaterConstraints, getHydratedFields } from 'utilities'
import {validateRepeaterConstraints} from './repeaterConstraints'
import {resolveFieldConditions} from '@tecuity/wizard-utilities'
import startOfDay from 'date-fns/startOfDay'
import differenceInDays from 'date-fns/differenceInDays'
import differenceInMonths from 'date-fns/differenceInMonths'
import isAfter from 'date-fns/isAfter'
import isBefore from 'date-fns/isBefore'
import {getWebUserRecordByType, updateWizardMeta} from './WizardActions'

/*
This file contains validation objects for validating field inputs.
These validators can be used for both inline, tab-off, and server validation.
To define a new validator, adhere to the following function structure:

[Validator Name]: ([Field Value], [Field Type], [{Object containing properties needed by the validation function}]) => {
  return ([Boolean: TRUE for passed validation, FALSE for failed validation], [optional string: A human-readable error message to be returned to the user.])
}

As described above, validators should always be pure functions, and should only return
either [true] or [false] with an optional error message if applicable. If you intend the
validation to transform the input value in some way, use the Mask() object.

Mask() functions may call Validate() functions, but Validate() functions should never
call Mask() functions. This ensures that when calling a Validate() function you may always
safely expect a boolean response.

*/

const valueIsNotEmptyOrNull = (value) =>
  (value !== '' && value !== null && value !== undefined && typeof value !== 'object') ||
  value === 0 ||
  value === '0'

const valueIsPossiblyValidZip = (value) => {
  try {
    return valueIsNotEmptyOrNull(value) && String(value).length >= 5
  } catch (e) {
    console.warn(e)
    return false
  }
}

const sanitizeNumber = (value) => {
  let returnVal
  returnVal = value.toString()
  if (returnVal.includes('.') && returnVal.split('.').length <= 2) {
    try {
      returnVal = Number.parseFloat(
        value.toString().replace(/,/g, '').replace(/\$/g, '').replace(/ /g, ''),
        10
      )
    } catch (e) {
      returnVal = value
    } finally {
      return returnVal
    }
  } else {
    try {
      returnVal = Number.parseInt(
        value.toString().replace(/,/g, '').replace(/\$/g, '').replace(/ /g, ''),
        10
      )
    } catch (e) {
      returnVal = value
    } finally {
      return returnVal
    }
  }
}

const getTrimmedValue = (value = '') => (!value ? '' : value.trim())

const valueIsANumber = (value) => !!/^-?[\d.]+(?:e-?\d+)?$/.test(value)

const emptyExceptDefaultState = (value, field) => {
  for (const key in value) {
    if (key != 'STATE' && key != 'COUNTRY' && valueIsNotEmptyOrNull(getTrimmedValue(value[key]))) {
      return false
    }
  }
  if (getTrimmedValue(value.STATE) === field.defaultState) {
    return true
  }
}

export const VALIDATE = {
  notEmptyOrNull: (value, field, props = {}, callback) => {
    //checks that the input is not empty.
    let testValue = value
    if (field.type === 'confirmFields') {
      testValue = value.val1
    }
    if (valueIsNotEmptyOrNull(testValue)) {
      callback(true, '')
    } else {
      callback(
        false,
        props.errorMessage && props.errorMessage !== ''
          ? props.errorMessage
          : 'This field is required.'
      )
    }
  },
  mustBeSelected: (value, field, props = {}, callback) => {
    const selectedOptionExists = field.options.find(
      (option) => option.value.toString() === value.toString()
    )
    if (value === 'default' || !selectedOptionExists) {
      callback(
        false,
        props.errorMessage && props.errorMessage !== ''
          ? props.errorMessage
          : 'This field is required.'
      )
    } else {
      callback(true, '')
    }
  },
  numberLessThan: (value, field, props = {}, callback) => {
    if (value) {
      //Checks that a given number is less than, but not equal to, a number provided on the "max" property of the props object.
      let sanitizedNumber = sanitizeNumber(value)
      if (!valueIsANumber(sanitizedNumber)) {
        callback(false, 'Please provide a valid number to continue.')
      } else if (sanitizedNumber <= props.max) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage && props.errorMessage !== ''
            ? props.errorMessage
            : 'This number must be less than ' + props.max + '.'
        )
      }
    } else {
      callback(null, '')
    }
  },
  numberGreaterThan: (value, field, props = {}, callback) => {
    if (value) {
      let sanitizedNumber = sanitizeNumber(value)
      if (!valueIsANumber(sanitizedNumber)) {
        callback(false, 'Please provide a valid number to continue.')
      } else if (sanitizedNumber >= props.min) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage && props.errorMessage !== ''
            ? props.errorMessage
            : 'This number must be greater than ' + props.min + '.'
        )
      }
    } else {
      callback(null, '')
    }
  },
  mustBeTrue: (value, field, props = {}, callback) => {
    //Checks that the given value is true. This is a "truthy" check, so the value may be either a string or boolean true.
    //You may include a custom error message on the props object
    if (value === true || value === 'true') {
      callback(true, '')
    } else {
      callback(
        false,
        props.errorMessage && props.errorMessage !== ''
          ? props.errorMessage
          : 'This field must be checked'
      )
    }
  },
  valuesMatch: (value, field, props = {}, callback) => {
    //Checks that two given values match each other. The value should be an object with 'val1' and 'val2' properties.
    //This is not a truthy check. The datatypes of the two values MUST match.
    //By default this validator is case-sensitive, but you can pass "caseSensitive: false" on the props parameter to deactivate the case-sensitivity
    let newValue = { ...value }
    newValue.val1 = value.val1.trim()
    newValue.val2 = value.val2.trim()
    if (props.caseSensitive === false) {
      newValue.val1 = value.val1.toLowercase()
      newValue.val2 = value.val2.toLowercase()
    }
    if (newValue.val1 === newValue.val2) {
      callback(true, '')
    } else {
      callback(
        false,
        props.errorMessage && props.errorMessage !== ''
          ? props.errorMessage
          : 'These two fields must match exactly'
      )
    }
  },
  mustNotEqualString: (value, field, props = {}, callback) => {
    //Checks that the given value does not equal a given string. The string must be passed on the props object under the "string" property
    //Because this validator is so dynamic, it is recommended that you pass in a custom errorMessage on the "errorMessage" property of the props object
    //By default this validator is case-sensitive, but you can pass "caseSensitive: false" on the props parameter to deactivate the case-sensitivity
    value = value.trim()
    if (props.caseSensitive === false) {
      value = value.toLowerCase()
    }
    if (value === props.string) {
      callback(false, props.errorMessage || props.string + ' is not a valid input.')
    } else {
      callback(true, '')
    }
  },
  mailingAddressIsRequired: (value, field, props = {}, callback) => {
    VALIDATE.addressIsRequired(value.MAIL_ADDR, field, props, callback)
  },
  addressIsRequired: (value, field, props = {}, callback) => {
    const isForeign = value.COUNTRY.toLowerCase() !== 'united states'
    if (!valueIsNotEmptyOrNull(getTrimmedValue(value.COUNTRY))){
      callback(false, 'Please select a valid country.')
    }
    if (isForeign) {
      if (valueIsNotEmptyOrNull(getTrimmedValue(value.ADDR1))) {
        callback(true, '')
      } else {
        callback(false, props.errorMessage || 'Address 1 is required')
      }
    } else {
      if (valueIsNotEmptyOrNull(getTrimmedValue(field.defaultState)) && !value.STATE){
        value.STATE = field.defaultState
      }

      if (
        valueIsNotEmptyOrNull(getTrimmedValue(value.ADDR1)) &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.CITY)) &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.STATE)) &&
        valueIsPossiblyValidZip(getTrimmedValue(value.POSTAL_CODE))
      ) {
        callback(true, '')
      } else {
        callback(false, props.errorMessage || 'Please provide a full address to continue.')
      }
    }
  },
  mailingAddressIsValid: (value, field, props = {}, callback) => {
    VALIDATE.addressIsValid(value.MAIL_ADDR, field, props, callback)
  },
  addressIsValid: (value, field, props = {}, callback) => {
    let isCompleteAddress =
      valueIsNotEmptyOrNull(getTrimmedValue(value.ADDR1)) &&
      ((valueIsNotEmptyOrNull(getTrimmedValue(value.CITY)) &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.STATE))) ||
        valueIsNotEmptyOrNull(getTrimmedValue(value.POSTAL_CODE)))

    let isModified =
      valueIsNotEmptyOrNull(getTrimmedValue(value.ADDR1)) ||
      valueIsNotEmptyOrNull(getTrimmedValue(value.CITY)) ||
      valueIsNotEmptyOrNull(getTrimmedValue(value.POSTAL_CODE))

    const stateNotReplaceable = field.defaultState && field.disableState
    const allowAddressValidationBypass = (props.allowAddressValidationBypass || props.allowAgentAddressMailValidationBypass || props.allowAgentAddressPrimaryValidationBypass) && process.env.ALLOW_ADDRESS_VALIDATION_BYPASS_OPTION;
    const isBypassAcknowledged = value.MAIL_ADDR_BYPASS_YN === 'Y'
    if (value.COUNTRY.toLowerCase() === 'united states') {
      if (!isCompleteAddress && isModified) {
        callback(false, props.errorMessage || 'Please provide a complete address.')
      } else if (!isModified || (isBypassAcknowledged && allowAddressValidationBypass)) {
        callback(true, '', {
          value: { ...value },
          confirmed: true,
        })
      } else {
        api.VALIDATE(
          'address',
          value,
          (data) => {
            // console.log(data);
            if (data.valid) {
              const correctedState = (data.payload.corrected || {}).STATE
              if (field.disableState && value.STATE !== correctedState) {
                callback(
                  false,
                  `Provided ZIP code is not valid in the selected state of ${value.STATE}`,
                  {
                    value: {
                      ...value,
                      ...data.payload.corrected,
                      STATE: stateNotReplaceable ? value.STATE : data.payload.corrected.STATE,
                    },
                    infoMessage: data.payload.feedback.join('\n'),
                  }
                )
              } else {
                callback(true, '', {
                  value: {
                    ...value,
                    ...data.payload.corrected,
                    STATE: stateNotReplaceable ? value.STATE : data.payload.corrected.STATE,
                  },
                  infoMessage: data.payload.feedback.join('\n'),
                })
              }
            } else {
              callback(false, data.error, {
                value: {
                  ...value,
                  ...data.payload.corrected,
                  STATE: stateNotReplaceable ? value.STATE : data.payload.corrected.STATE,
                },
                infoMessage: null,
              })
            }
          },
          (err) => {
            callback(
              false,
              'Something went wrong while validating your address. If this problem persists, please contact customer support.'
            )
          }
        )
      }
    } else {
      callback(true, '')
    }
  },
  partialMailingAddressNotAllowed: (value, field, props = {}, callback) => {
    VALIDATE.partialAddressNotAllowed(value.MAIL_ADDR, field, props, callback)
  },
  partialAddressNotAllowed: (value, field, props = {}, callback) => {
    const addr1IsEmpty = {
      value: !valueIsNotEmptyOrNull(getTrimmedValue(value.ADDR1)),
      name: 'Address 1',
    }
    const cityIsEmpty = { value: !valueIsNotEmptyOrNull(getTrimmedValue(value.CITY)), name: 'City' }
    const stateIsEmpty = {
      value: !valueIsNotEmptyOrNull(getTrimmedValue(value.STATE)),
      name: 'State',
    }
    const postalIsEmpty = {
      value: !valueIsNotEmptyOrNull(getTrimmedValue(value.POSTAL_CODE)),
      name: 'ZIP code',
    }
    const anyEmpty =
      addr1IsEmpty.value || cityIsEmpty.value || stateIsEmpty.value || postalIsEmpty.value
    const allEmpty =
      addr1IsEmpty.value && cityIsEmpty.value && stateIsEmpty.value && postalIsEmpty.value
    // If there's a default state, consider the address empty/valid.
    if (emptyExceptDefaultState(value, field)) {
      callback(null, '')
    } else if (value.COUNTRY.toLowerCase() === 'united states' || value.COUNTRY.toLowerCase() === 'usa') {
      if (allEmpty) {
        callback(null, '')
      } else if (!anyEmpty) {
        callback(true, '')
      } else {
        let fields = [addr1IsEmpty, cityIsEmpty, stateIsEmpty, postalIsEmpty]
        let numEmpties = 0
        fields.forEach((field) => {
          if (field.value) {
            numEmpties++
          }
        })
        let empties = fields.filter((field) => field.value)

        if (numEmpties > 2) {
          let error = ''
          empties.forEach((empty, i) => {
            error = `${error}${
              i === empties.length - 1 ? 'and ' + empty.name + ' are required.' : empty.name + ', '
            }`
          })
          callback(false, props.errorMessage || `Please provide a complete address, ${error}`)
        } else if (numEmpties === 2) {
          callback(
            false,
            props.errorMessage ||
              `Please provide a complete address, ${empties[0].name} and ${empties[1].name} are required.`
          )
        } else if (numEmpties === 1) {
          callback(
            false,
            props.errorMessage ||
              `Please provide a complete address, ${empties[0].name} is required.`
          )
        } else {
          callback(true, '')
        }
      }
    } else {
      //Foreign
      if (addr1IsEmpty.value) {
        callback(false, `Please provide a value in Address 1`)
      } else {
        callback(true, '')
      }
    }
  },
  registeredAgentAddressIsValid: (value, field, props = {}, callback) => {
    if (
      value.TYPE_ID.toString() === '460' &&
      value.CRA_ID.toString() === '0' &&
      field.addingAgent
    ) {
      const validatePrincipleAgainstAMS = props.principalAddressAMS || process.env.RA_PRINCIPAL_ADDRESS_AMS;
      const validateMailingAddress = props.mailingAddressRequired
      let isIndividual = value.INDIVIDUAL_YN.toUpperCase() == 'Y'
      let hasInputFirstAndLastNames =
        valueIsNotEmptyOrNull(value.FIRST_NAME) && valueIsNotEmptyOrNull(value.LAST_NAME)
      let hasSelectedOrganization = value.ORGANIZATION
        ? value.ORGANIZATION.SOURCE_ID !== 0 && value.ORGANIZATION !== null
        : false
      let hasInputAddress =
        valueIsNotEmptyOrNull(getTrimmedValue(value.ADDR1)) &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.CITY)) &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.STATE)) &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.POSTAL_CODE))
      let hasInputMailingAddress =
        valueIsNotEmptyOrNull(getTrimmedValue(value.MAIL_ADDR.ADDR1)) &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.MAIL_ADDR.CITY)) &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.MAIL_ADDR.STATE)) &&
        valueIsNotEmptyOrNull(getTrimmedValue(value.MAIL_ADDR.POSTAL_CODE))
      const validateMailAddressRA = () => {
        if (validateMailingAddress || validatePrincipleAgainstAMS) {
          if (hasInputMailingAddress) {
            let promises = []
            if(validateMailingAddress){
              promises.push(
                new Promise((resolve) => {
                  VALIDATE.addressIsValid(value.MAIL_ADDR, field, props, (isValid, error, payload) => {
                    resolve({ isValid, error, payload, id: "mailing" })
                  })
                })
              )
            }
            if(validatePrincipleAgainstAMS){
              promises.push(
                new Promise((resolve) => {
                  VALIDATE.addressIsValid(value, field, props, (isValid, error, payload) => {
                    resolve({ isValid, error, payload, id: "principle" })
                  })
                })
              )
            }
            Promise.all(promises)
            .then((returnedAddresses) => {
              const mailAddress = returnedAddresses.find(a => a.id === "mailing")
              const principleAddress = returnedAddresses.find(a => a.id === "principle")
              let payload = mailAddress ? mailAddress.payload : null
              let principlePayload = principleAddress ? principleAddress.payload : null;
              if (payload && payload.value) {
                const newState = mailAddress.payload.value.STATE
                payload = {
                  value: {
                    ...value,
                    ...(
                      principlePayload && principlePayload.value ?
                      {
                        ADDR1: principlePayload.value.ADDR1,
                        ADDR2: principlePayload.value.ADDR2,
                        ADDR3: principlePayload.value.ADDR3,
                        CITY: principlePayload.value.CITY,
                        STATE: principlePayload.value.STATE,
                        COUNTRY: principlePayload.value.COUNTRY,
                        COUNTY: principlePayload.value.COUNTY,
                        POSTAL_CODE: principlePayload.value.POSTAL_CODE,
                      }
                      : {}
                    ),
                    MAIL_ADDR: {
                      ADDR1: mailAddress.payload.value.ADDR1,
                      ADDR2: mailAddress.payload.value.ADDR2,
                      ADDR3: mailAddress.payload.value.ADDR3,
                      CITY: mailAddress.payload.value.CITY,
                      STATE: process.env.RA_MAILING_STATE_DISABLED
                        ? value.MAIL_ADDR.STATE
                        : newState,
                      COUNTRY: mailAddress.payload.value.COUNTRY,
                      COUNTY: mailAddress.payload.value.COUNTY,
                      POSTAL_CODE: mailAddress.payload.value.POSTAL_CODE,
                    },
                  },
                }
                if (process.env.RA_MAILING_STATE_DISABLED && value.MAIL_ADDR.STATE !== newState) {
                  callback(
                    false,
                    `Provided ZIP code is not valid in the selected state of ${value.MAIL_ADDR.STATE}`,
                    payload
                  )
                }
              }
              if ((validateMailingAddress ? mailAddress.isValid : true) && (validatePrincipleAgainstAMS ? principleAddress.isValid : true)) {
                callback(true, '', payload)
              } else {
                let messages = []
                //If these error messages change or become dynamic AddressBlock.js ShowAddressValidationBypass must be updated
                if(validateMailingAddress && !mailAddress.isValid){
                  messages.push('Mailing address must be a valid address to continue.')
                }
                if(validatePrincipleAgainstAMS && !principleAddress.isValid){
                  messages.push('Street address must be a valid address to continue.')
                }
                callback(false, messages.join("\n"))
              }
            })
          } else {
            callback(false, 'A mailing address is required.')
          }
        } else {
          callback(true, '')
        }
      }

      if (isIndividual) {
        if (hasInputAddress && hasInputFirstAndLastNames) {
          validateMailAddressRA()
        } else if (hasInputAddress && !hasInputFirstAndLastNames) {
          callback(
            false,
            'Please provide the first and last name of the registered agent to continue.'
          )
        } else if (!hasInputAddress && hasInputFirstAndLastNames) {
          callback(
            false,
            props.errorMessage ||
              'Please provide the full address of the registered agent to continue.'
          )
        } else {
          callback(false, 'Provide the name and address of the registered agent to continue.')
        }
      } else if (!isIndividual) {
        if (hasInputAddress && hasSelectedOrganization) {
          // VALIDATE.addressIsValid(
          // 	value,
          // 	field,
          // 	props,
          // 	(isValid, error, payload) => {
          // 		//console.log('Got: ', isValid, error, payload);
          // 		callback(isValid, error, payload);
          // 	}
          // );
          validateMailAddressRA()
        } else if (!hasInputAddress && hasSelectedOrganization) {
          callback(
            false,
            props.errorMessage ||
              'Please provide the full address of the registered agent to continue'
          )
        } else if (hasInputAddress && !hasSelectedOrganization) {
          callback(
            false,
            'Please provide the name of the registered agent organization to continue.'
          )
        } else {
          callback(
            false,
            'Please provide the registered agent organization name and address to continue.'
          )
        }
      }
    } else if (value.CRA_ID !== '0' && value.CRA_ID !== 0) {
      callback(true, '')
    } else {
      callback(true, '')
    }
  },
  registeredAgentIsRequired: (value, field, props = {}, callback) => {
    let isNonCommercial = value.TYPE_ID.toString() === '460'
    let craId = value.CRA_ID.toString()
    let hasSelectedRa = craId !== '0' && craId !== '' && craId
    let isIndividual = value.INDIVIDUAL_YN.toUpperCase() == 'Y'
    let hasInputFirstAndLastNames =
      valueIsNotEmptyOrNull(value.FIRST_NAME) && valueIsNotEmptyOrNull(value.LAST_NAME)
    let hasSelectedOrganization = value.ORGANIZATION
      ? value.ORGANIZATION.SOURCE_ID !== 0 && value.ORGANIZATION !== null
      : false
    const regex = /(.{1,}?)@(.{1,}?)\.(.{1,})/g
    let hasInputValidEmail = process.env.RA_EMAIL
      ? valueIsNotEmptyOrNull(value.EMAIL) && regex.test(value.EMAIL)
      : true
    let hasInputAddress =
      valueIsNotEmptyOrNull(getTrimmedValue(value.ADDR1)) &&
      valueIsNotEmptyOrNull(getTrimmedValue(value.CITY)) &&
      valueIsNotEmptyOrNull(getTrimmedValue(value.STATE)) &&
      valueIsNotEmptyOrNull(getTrimmedValue(value.POSTAL_CODE))

    let isValid = null
    let errorMessage = []

    const setCallback = (valid, err) => {
      if (!valid) {
        isValid = valid
        errorMessage.push(err)
      } else if (isValid === null && valid === true) {
        isValid = valid
      }
    }

    if (isNonCommercial) {
      if (hasSelectedRa) {
        setCallback(true, '')
      } else if (!value.NEW && !value.SELECTED && !field.addingAgent) {
        // this checks for a case the MT had where the user entered a new RA, but the NEW field was false which resulted in the image generated showing "No Agent"
        setCallback(false, props.errorMessage || 'Select an existing registered agent or fill out the fields above to create a new registered agent before continuing.')        
      } else if (isIndividual) {
        if (hasInputAddress && hasInputFirstAndLastNames && hasInputValidEmail) {
          setCallback(true, '')
        }
        if (!hasInputAddress) {
          setCallback(false, 'Please provide the full address of the registered agent to continue.')
        }
        if (!hasInputFirstAndLastNames) {
          setCallback(
            false,
            'Please provide the first and last names of the registered agent to continue.'
          )
        }
        if (field.addingAgent && !hasInputValidEmail) {
          setCallback(false, 'Input a valid email to continue.')
        }
      } else if (!isIndividual) {
        if (hasInputAddress && hasSelectedOrganization && hasInputValidEmail) {
          setCallback(true, '')
        }
        if (!hasInputAddress) {
          setCallback(false, 'Please provide the full address of the registered agent to continue.')
        }
        if (!hasSelectedOrganization) {
          setCallback(
            false,
            'Please provide the name of the registered agent organization to continue.'
          )
        }
        if (field.addingAgent && !hasInputValidEmail) {
          setCallback(false, 'Input a valid email to continue.')
        }
      } else {
        setCallback(
          false,
          props.errorMessage || 'Select an existing registered agent or fill out the fields above to create a new registered agent before continuing.'
        )
      }
    } else {
      if (hasSelectedRa) {
        setCallback(true, '')
      } else {
        setCallback(false, props.errorMessage || 'Select a registered agent to continue.')
      }
    }
    callback(isValid, errorMessage.join('\n'))
  },
  registeredAgentNotSoS: (value, field, props = {}, callback) => {
    let craId = value.CRA_ID.toString()
    if (craId === '1') {
      callback(
        false,
        'Secretary of State cannot be used as a registered agent. Select a different agent.'
      )
    } else {
      callback(true, '')
    }
  },
  noRegulatedWords: (value, field, props = {}, callback) => {
    //Test that a given value does not contain regulated words surrounded by whitespace.
    let metadata = store.getState().wizard.wizard.meta
    const filingTypeIdSrcField = store.getState().wizard.wizard.fields[props.filingTypeId]
    const filingTypeIdVal = filingTypeIdSrcField ? filingTypeIdSrcField.value : null
    const { FILING_TYPE_ID = 0 } = metadata
    const filingTypeId =
      filingTypeIdVal && filingTypeIdVal !== '0' ? filingTypeIdVal : FILING_TYPE_ID
    if (filingTypeId === 0) {
      callback(false, 'Business rule failed because no FILING_TYPE_ID found in MetaData.')
    } else if (value.val1 === '') {
      callback(false, 'Please provide a name')
    } else {
      api.VALIDATE(
        'RegulatedWords',
        {
          FILING_NAME: typeof value.val1 === 'string' ? value.val1 : value,
          FILING_TYPE_ID: filingTypeId,
        },
        (data) => {
          if (data.valid) {
            callback(true, '')
          } else {
            callback(false, data.error)
          }
        }
      )
    }
  },
  hasNameEnding: (value, field, props = {}, callback) => {
    //Tests that the given value contains the designation appropriate for the entity type. (LLC, LLP, CO, etc...)
    let metadata = store.getState().wizard.wizard.meta
    const filingTypeIdSrcField = store.getState().wizard.wizard.fields[props.filingTypeId]
    const filingTypeIdVal = filingTypeIdSrcField ? filingTypeIdSrcField.value : null
    const { FILING_TYPE_ID = 0 } = metadata
    const filingTypeId =
      filingTypeIdVal && filingTypeIdVal !== '0' ? filingTypeIdVal : FILING_TYPE_ID
    if (filingTypeId === 0) {
      callback(false, 'Business rule failed because no FILING_TYPE_ID found in MetaData.')
    } else {
      let payload = {}
      if (typeof value.val1 === 'string') {
        //If this is a confirm field
        payload = { FILING_NAME: value.val1, FILING_TYPE_ID: filingTypeId }
      } else {
        payload = { FILING_NAME: value, FILING_TYPE_ID: filingTypeId }
      }

      if (payload.FILING_NAME === '' || payload.FILING_TYPE_ID === 0) {
        callback(true, '')
      } else {
        api.VALIDATE('NameDesignation', payload, (data) => {
          if (data.valid) {
            callback(true, '')
          } else {
            callback(false, props.errorMessage ? props.errorMessage : data.error)
          }
        })
      }
    }
  },
  isValidName: (value, field, props = {}, callback) => {
    //Tests that the given value contains the designation appropriate for the entity type. (LLC, LLP, CO, etc...)
    let metadata = store.getState().wizard.wizard.meta
    const filingTypeIdSrcField = store.getState().wizard.wizard.fields[props.filingTypeId]
    const filingTypeIdVal = filingTypeIdSrcField ? filingTypeIdSrcField.value : null
    const { FILING_TYPE_ID = 0 } = metadata
    const filingTypeId =
      filingTypeIdVal && filingTypeIdVal !== '0' ? filingTypeIdVal : FILING_TYPE_ID
    if (filingTypeId === 0) {
      callback(false, 'Business rule failed because no FILING_TYPE_ID found in MetaData.')
    } else {
      let payload = {}
      const subFilingTypeIdSrcField = store.getState().wizard.wizard.fields[props.filingSubtypeId]
      const subFilingTypeIdVal = subFilingTypeIdSrcField ? subFilingTypeIdSrcField.value : null
      if (typeof value.val1 === 'string') {
        //If this is a confirm field
        payload = {
          ...metadata,
          FILING_NAME: value.val1,
          FIELD_NAME: field.name,
          RESERVED_FILING_ID: value.reservedNameId,
          FILING_TYPE_ID: filingTypeId,
          FILING_SUBTYPE_ID: subFilingTypeIdVal || 0,
          ADDITIONAL_MODE: field.hideAdditionalName ? null : value.additionalMode || null,
        }
      } else {
        payload = {
          ...metadata,
          FILING_NAME: value,
          FIELD_NAME: field.name,
          RESERVED_FILING_ID: value.reservedNameId,
          FILING_TYPE_ID: filingTypeId,
          FILING_SUBTYPE_ID: props.filingSubtypeId || 0,
          ADDITIONAL_MODE: field.hideAdditionalName ? null : value.additionalMode,
        }
      }
      if (payload.FILING_NAME === '' || payload.FILING_TYPE_ID === 0) {
        callback(true, '', { infoMessage: '', requireSupportingDocument: false })
      } else {
        api.VALIDATE('ValidEntityName', payload, (data) => {
          // if(data.payload){
          // 	if(data.payload.INTERNAL_REVIEW_REQUIRED){
          // 		store.dispatch({
          // 			type: 'ADD_INTERNAL_REVIEW_REQUIRED',
          // 			fieldId: field.fieldId
          // 		})
          // 	}else{
          // 		store.dispatch({
          // 			type: 'REMOVE_INTERNAL_REVIEW_REQUIRED',
          // 			fieldId: field.fieldId
          // 		})
          // 	}
          // }
          if (data.valid) {
            // console.log(data);
            callback(true, '', {
              infoMessage: data.payload.INFO_MESSAGE,
              requireSupportingDocument: data.requireSupportingDocument === true ? 'Y' : 'N',
            })
          } else {
            callback(false, props.errorMessage ? props.errorMessage : data.error, {
              infoMessage: '',
              requireSupportingDocument: data.requireSupportingDocument === true ? 'Y' : 'N',
            })
          }
        })
      }
    }
  },
  repeaterMinRows: (value, field, props = {}, callback) => {
    const numRows = field.trackChanges
      ? value.filter((row) => row._statusId !== 3).length
      : value.length
    if (numRows >= props.minRows) {
      callback(true, '')
    } else {
      if (props.noRowsIsValid && numRows === 0) {
        callback(null, '')
      } else {
        callback(
          false,
          props.errorMessage ||
            `You must add at least ${props.minRows} ${props.minRows === 1 ? 'entry' : 'entries'}.`
        )
      }
    }
  },
  repeaterMaxRows: (value, field, props = {}, callback) => {
    const numRows = field.trackChanges
      ? value.filter((row) => row._statusId !== 3).length
      : value.length
    if (numRows <= props.maxRows) {
      callback(true, '')
    } else {
      callback(false, props.errorMessage || `You may not add more than ${props.maxRows} entries.`)
    }
  },
  validateRepeaterFields: (value, field, props = {}, callback) => {
    const fields = store.getState().wizard.wizard.fields
    const rowValids = value.map((row, rowIndex) => {
      const isExempt = props.skipDeletedRows ? row._statusId === 3 : false
      const { template } = field
      return new Promise((rowResolve) => {
        if (isExempt) {
          rowResolve(
            template.map((fieldId) => ({ isValid: null, valids: [], errors: [], fieldId }))
          )
        } else {
          const cellValids = template
            .map((fieldId) => {
              const hydratedFields = getHydratedFields(row, template, fields)
              const field = resolveFieldLogic(fields[fieldId], { fields: hydratedFields, currentRepeaterRowIndex: rowIndex })
              const conditionsResolved = resolveFieldConditions(
                field.conditions,
                hydratedFields,
                field.conditionsOperator
              )
              if (conditionsResolved && field.active) {
                return new Promise((resolve) => {
                  loopThroughRules(
                    {
                      businessRules: field.businessRules,
                      value: row[fieldId],
                      field: { ...field, value: row[fieldId] },
                      event: 'onAdvance',
                      options: { currentRepeaterRowIndex: rowIndex }
                    },
                    (validation) => {
                      resolve({ ...validation, fieldId: field.fieldId })
                    }
                  )
                })
              }
              return null
            })
            .filter((x) => x)
          Promise.all(cellValids).then((valids) => {
            rowResolve(valids)
          })
        }
      })
    })
    Promise.all(rowValids).then((valids) => {
      const fullValids = valids.map((row) =>
        row.reduce(
          (obj, cell) => ({
            ...obj,
            rowValid: obj.rowValid === false ? false : !cell.valids.includes(false),
            cellValids: {
              ...obj.cellValids,
              [cell.fieldId]: {
                isValid: cell.valids.every((valid) => valid !== false),
                errors: cell.errors,
              },
            },
          }),
          { rowValid: true, cellValids: {} }
        )
      )
      const allRowsValid = fullValids.every((row) => row.rowValid)
      if (allRowsValid) {
        callback(true, '', { rowErrors: null })
      } else {
        callback(false, 'Please review your entries for errors.', { rowErrors: fullValids })
      }
    })
  },
  validateThirdPartySignaturesRepeater: (value, field, props = {}, callback) => {
    if (value.method === 'signer') {
      VALIDATE.validateRepeaterFields(value.signers || [], field, props, callback)
    } else {
      callback(null, '')
    }
  },
  nameNotTaken: (value, field, props = {}, callback) => {
    if (value.val1 === '') {
      callback(true, '', { infoMessage: '', requireSupportingDocument: false })
    } else {
      const RECORD_NUM = getCurrentWizardAmendmentNum()
      const RECORD_ID = field.allowReservedNameEditing ? value.reservedNameId : 0
      const filingTypeIdSrcField = store.getState().wizard.wizard.fields[props.filingTypeId]
      let metadata = store.getState().wizard.wizard.meta
      const { FILING_TYPE_ID: defaultFilingTypeId = 0 } = metadata
      const FILING_TYPE_ID = filingTypeIdSrcField ? filingTypeIdSrcField.value : defaultFilingTypeId
      if (store.getState().wizard.wizard.category === "trademark" && process.env.USE_TRADEMARK_OWNER_VALIDATION) {
        let TRADEMARK_NAME = value.val1
        metadata = {...metadata, TRADEMARK_NAME: TRADEMARK_NAME};
        store.dispatch(updateWizardMeta(metadata));
        
        const applicantTypeID = store.getState().wizard.wizard.fields[metadata.APPLICANT_TYPE_ID_FIELD].value || "0"
        const trademarkApplicantSelectedOwner = store.getState().wizard.wizard.fields[metadata.SELECTED_OWNER_NAME_FIELD].value
        const trademarkApplicantFilingID = trademarkApplicantSelectedOwner ? trademarkApplicantSelectedOwner.SOURCE_ID : "0"
        const trademarkApplicantFilingTypeID = trademarkApplicantSelectedOwner ? trademarkApplicantSelectedOwner.FILING_TYPE_ID : "0"
        const serviceMarkYN = store.getState().wizard.wizard.fields[metadata.SERVICE_MARK_YN_FIELD].value
        
        if (applicantTypeID !== "0") {
          metadata = {...metadata, TRADEMARK_APPLICANT_TYPE_ID: applicantTypeID};
          store.dispatch(updateWizardMeta(metadata));
        }
        if (applicantTypeID === "818" && trademarkApplicantFilingID !== "0") {
          metadata = {...metadata, TRADEMARK_APPLICANT_FILING_ID: trademarkApplicantFilingID};
          store.dispatch(updateWizardMeta(metadata));
        }
        if (applicantTypeID === "818" && trademarkApplicantFilingTypeID !== "0") {
          metadata = {...metadata, TRADEMARK_APPLICANT_FILING_TYPE_ID: trademarkApplicantFilingTypeID};
          store.dispatch(updateWizardMeta(metadata));
        }
        if (serviceMarkYN) {
          metadata = {...metadata, SERVICE_MARK_YN: serviceMarkYN};
          store.dispatch(updateWizardMeta(metadata));
        }
      }
      var payload = {
        ...metadata,
        FILING_NAME: value.val1,
        RESERVED_FILING_ID: value.reservedNameId,
        RECORD_NUM: RECORD_NUM === 0 ? '' : RECORD_NUM,
        RECORD_ID: RECORD_ID,
        FIELD_NAME: field.name,
        CORRECTION_YN: store.getState().wizard.correctionMode,
        FILING_TYPE_ID
      }
      
      if(process.env.COMPARE_FOREIGN_NAME_IN_ENTITY_NAME_VALIDATION){
        const state = store.getState();
        const fields = state.wizard.wizard.fields;
        let foreignNameField = Object.values(fields).find(f => f.name === process.env.FOREIGN_NAME_FIELD_NAME);
        if (foreignNameField) {
          payload["FOREIGN_NAME"] = foreignNameField.value.val1
        }
      }
      
      api.VALIDATE('NameLookup', payload, (data) => {
        if (data.valid) {
          callback(true, '', {
            infoMessage: data.payload.INFO_MESSAGE,
            requireSupportingDocument: data.requireSupportingDocument === true ? 'Y' : 'N',
          })
        } else {
          callback(false, props.errorMessage ? props.errorMessage : data.error, {
            infoMessage: '',
            requireSupportingDocument: data.requireSupportingDocument === true ? 'Y' : 'N',
          })
        }
      })
    }
  },
  nameRequired: (value, field, props = {}, callback) => {
    if (
      (valueIsNotEmptyOrNull(value.FIRST_NAME) || props.onlyLastNameRequired) &&
      valueIsNotEmptyOrNull(value.LAST_NAME)
    ) {
      callback(true, '')
    } else {
      callback(
        false,
        props.errorMessage !== ''
          ? props.errorMessage
          : props.onlyLastNameRequired
          ? 'Last name is required.'
          : 'First and last name are required.'
      )
    }
  },
  nameOrgRequired: (value, field, props = {}, callback) => {
    const isOrganization =
      field.type === 'NameQualifiedOrg' ? value.ORG_TYPE.toString() === '2' : value.ORG_YN
    if (isOrganization) {
      if (valueIsNotEmptyOrNull(value.ORG_NAME)) {
        callback(true, '')
      } else {
        callback(false, 'Organization is required')
      }
    } else {
      VALIDATE['nameRequired'](value, field, props, (isValid, error) => {
        callback(isValid, error)
      })
    }
  },
  nameQualifiedOrgRequired: (value, field, props = {}, callback) => {
    if (value.ORG_TYPE.toString() === '1') {
      if (value.SELECTED) {
        callback(true, '')
      } else {
        callback(false, 'An item from the search must be selected')
      }
    } else {
      VALIDATE['nameOrgRequired'](value, field, props, (isValid, error) => {
        callback(isValid, error)
      })
    }
  },
  searchItemRequired: (value, field, props = {}, callback) => {
    if (field.multiSelect ? value.length : value) {
      callback(true, '')
    } else {
      callback(false, props.errorMessage || 'An item from the search must be selected')
    }
  },
  validFeinNum: (value, field, props = {}, callback) => {
    if (!valueIsNotEmptyOrNull(value)) {
      callback(null, '')
    } else {
      let isValidFein = /^(0[1-9]|[1-9]\d)-\d{7}$/.test(value) && value.length === 10
      if (isValidFein) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== ''
            ? props.errorMessage
            : 'Please provide a properly formatted FEIN number to continue.'
        )
      }
    }
  },
  validPhoneNumber: (value, field, props = {}, callback) => {
    let isValidPhoneNumber = /\([2-9]\d{2}\) [2-9]\d{2}-\d{4}/.test(value) && value.length === 14
    if (isValidPhoneNumber) {
      callback(true, '')
    } else {
      callback(
        false,
        props.errorMessage !== '' ? props.errorMessage : 'Provide a valid 10-digit phone number to continue.'
      )
    }
  },
  validPhoneNumberIfPresent: (value, field, props = {}, callback) => {
    if(value && typeof value === 'string' && value.length > 0){
      let isValidPhoneNumber = /\([2-9]\d{2}\) [2-9]\d{2}-\d{4}/.test(value) && value.length === 14
      if (isValidPhoneNumber) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== '' ? props.errorMessage : 'Provide a valid 10-digit phone number to continue.'
        )
      }
    }else{
      callback(null, '')
    }
  },
  enforceMaxDaysAfterToday: (value, field, props = {}, callback) => {
    if (field.maxAfterToday === '' || value === null || value === '') {
      callback(null, '')
    } else {
      let date = startOfDay(new Date(value))
      let today
      const stateTimeZone = process.env.STATE_TIMEZONE
      if(stateTimeZone){
        let localDate = new Date()    
        let stateLocalString = localDate.toLocaleString('en-US', {timeZone: stateTimeZone})
        today = startOfDay(new Date(stateLocalString))
      }
      else{
        today = startOfDay(new Date())
      } 
           
      var daysAfterToday = differenceInDays(date, today)
      if (daysAfterToday <= props.maxAfterToday) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== '' ? props.errorMessage : 'Selected date is outside valid range.'
        )
      }
    }
  },
  enforceMaxDaysBeforeToday: (value, field, props = {}, callback) => {
    if (field.maxBeforeToday == '' || value === null || value === '') {
      callback(null, '')
    } else {
      let date = startOfDay(new Date(value))
      let now
      const stateTimeZone = process.env.STATE_TIMEZONE
      if(stateTimeZone){
        let localDate = new Date()    
        let stateLocalString = localDate.toLocaleString('en-US', {timeZone: stateTimeZone})
        now = startOfDay(new Date(stateLocalString))
      }
      else{
        now = startOfDay(new Date())
      }
      
      var daysBeforeToday = differenceInDays(now, date)
      if (daysBeforeToday <= props.maxBeforeToday) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== '' ? props.errorMessage : 'Selected date is outside valid range.'
        )
      }
    }
  },
  enforceMaxMonthsAfterToday: (value, field, props = {}, callback) => {
    if (field.maxAfterToday === '' || value === null || value === '') {
      callback(null, '')
    } else {
      let date = startOfDay(new Date(value))
      let today = startOfDay(new Date())
      var monthsAfterToday = differenceInMonths(date, today)
      if (monthsAfterToday <= props.maxAfterToday) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== '' ? props.errorMessage : 'Selected date is outside valid range.'
        )
      }
    }
  },
  enforceMaxMonthsBeforeToday: (value, field, props = {}, callback) => {
    if (field.maxBeforeToday == '' || value === null || value === '') {
      callback(null, '')
    } else {
      let date = startOfDay(new Date(value))
      let now = startOfDay(new Date())
      var monthsBeforeToday = differenceInMonths(now, date)
      if (monthsBeforeToday <= props.maxBeforeToday) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== '' ? props.errorMessage : 'Selected date is outside valid range.'
        )
      }
    }
  },
  enforceMinDaysBeforeToday: (value, field, props = {}, callback) => {
    if (field.minBeforeToday == '' || value === null || value === '') {
      callback(null, '')
    } else {
      let date = startOfDay(new Date(value))
      let now = startOfDay(new Date())
      var daysBeforeToday = differenceInDays(now, date)
      if (daysBeforeToday >= props.minBeforeToday) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== '' ? props.errorMessage : 'Selected date is outside valid range.'
        )
      }
    }
  },
  enforceMinDaysAfterToday: (value, field, props = {}, callback) => {
    if (field.minAfterToday === '' || value === null || value === '') {
      callback(null, '')
    } else {
      let date = startOfDay(new Date(value))
      let today = startOfDay(new Date())
      var daysAfterToday = differenceInDays(date, today)
      if (daysAfterToday >= props.minAfterToday) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== '' ? props.errorMessage : 'Selected date is outside valid range.'
        )
      }
    }
  },
  enforceMinMonthsAfterToday: (value, field, props = {}, callback) => {
    if (field.minAfterToday === '' || value === null || value === '') {
      callback(null, '')
    } else {
      let date = startOfDay(new Date(value))
      let today = startOfDay(new Date())
      var monthsAfterToday = differenceInMonths(date, today)
      if (monthsAfterToday >= props.minAfterToday) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== '' ? props.errorMessage : 'Selected date is outside valid range.'
        )
      }
    }
  },
  enforceMinMonthsBeforeToday: (value, field, props = {}, callback) => {
    if (field.minBeforeToday == '' || value === null || value === '') {
      callback(null, '')
    } else {
      let date = startOfDay(new Date(value))
      let now = startOfDay(new Date())
      var daysBeforeToday = differenceInMonths(now, date)
      if (daysBeforeToday >= props.minBeforeToday) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== '' ? props.errorMessage : 'Selected date is outside valid range.'
        )
      }
    }
  },
  enforceSignatureDateRange: (value, field, props = {}, callback, { event, subField }) => {
    const rules = ['enforceMaxDaysBeforeToday', 'enforceMaxDaysAfterToday']

    const rowPromises = value.map(
      (row) =>
        new Promise((resolve) => {
          const rulePromises = rules.map(
            (ruleId) =>
              new Promise((subResolve) => {
                getIsValid(ruleId, row.date, field, props, (isValid, errorMessage) => {
                  subResolve({ ruleId, isValid, errorMessage })
                })
              })
          )
          Promise.all(rulePromises).then((ruleResolves) => {
            const bothValid = !ruleResolves.some((result) => result.isValid === false)
            let errorMessage = !bothValid
              ? ruleResolves.find((result) => result.isValid === false).errorMessage
              : ''
            resolve({ ruleResolves, bothValid, errorMessage })
          })
        })
    )
    Promise.all(rowPromises).then((rowResolves) => {
      const allValid = !rowResolves.some((result) => result.bothValid === false)
      callback(allValid, rowResolves)
    })
  },
  validZipCode: (value, field, props = {}, callback) => {
    let isZipCode = value === '' ? true : false
    isZipCode =
      /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(value) && (value.length === 10 || value.length === 5)
    if (isZipCode) {
      callback(true, '')
    } else {
      callback(
        false,
        props.errorMessage !== '' ? props.errorMessage : 'Correct this field to continue'
      )
    }
  },
  validAddressZipCode: (value, field, props = {}, callback) => {
    let newValue = value.POSTAL_CODE
    let isZipCode =
      /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(newValue) &&
      (newValue.length === 10 || newValue.length === 5)
    if (isZipCode) {
      callback(true, '')
    } else {
      callback(
        false,
        props.errorMessage !== '' ? props.errorMessage : 'Correct this field to continue'
      )
    }
  },
  validSocial: (value, field, props = {}, callback) => {
    if (!valueIsNotEmptyOrNull(value)) {
      callback(null, '')
    } else {
      let isValidSocial =
        /^(?!219-09-9999|078-05-1120)(?!666|000|9\d{2})\d{3}-(?!00)\d{2}-(?!0{4})\d{4}$/.test(
          value
        ) && value.length === 11
      if (isValidSocial) {
        callback(true, '')
      } else {
        callback(
          false,
          props.errorMessage !== ''
            ? props.errorMessage
            : 'Please provide a properly formatted Social Security Number to continue'
        )
      }
    }
  },
  enforceUploadPlaceholderRules: (value, field, props = {}, callback) => {
    const valueMethod =
      store.getState().wizard.requirePrintMailMethod.size > 0 ? 'mail' : value.method
    const method = field.type === 'UploadAndSubmit' && valueMethod ? valueMethod : null
    if (method === 'mail') {
      callback(true, '')
    } else {
      const uploadPlaceholders = getUploadsFromFields(store.getState().wizard.wizard.fields)
      const computedValue = (field.type === 'UploadAndSubmit' ? value.files : value) || {}
      if(process.env.PREVENT_WIZARD_SUBMISSION_WITH_UPLOAD_FAILURE) {
        uploadPlaceholders.map(
          (placeholder) => {
            if(computedValue[placeholder.fieldId] && computedValue[placeholder.fieldId].find(file => file.status === 'failed'))
            {
              callback(false, 'At least one upload failed')
            }
          }
        )
      }
      let rulePromises = uploadPlaceholders.map(
        (placeholder) =>
          new Promise((resolve) => {
            loopThroughRules(
              {
                businessRules: placeholder.businessRules,
                value: computedValue[placeholder.fieldId],
                field: placeholder,
                event: 'deferred',
              },
              (validation) => {
                resolve({ ...validation, fieldId: placeholder.fieldId })
              }
            )
          })
      )
      Promise.all(rulePromises).then((values) => {
        const allValid = values.reduce((isValid, currentVal) => {
          if (currentVal.valids.includes(false)) {
            return false
          } else {
            return isValid
          }
        }, true)
        const validations = values.reduce((valueObject, currentVal) => {
          valueObject[currentVal.fieldId] = {
            ...currentVal,
            isValid: !currentVal.valids.includes(false),
          }
          return valueObject
        }, {})
        callback(allValid, validations)
      })
    }
  },
  uploadRequired: (value, field, props = {}, callback) => {
    if (!Array.isArray(value)) {
      callback(false, 'Upload is required')
    } else {
      const atLeastOneUploadSucceeded = value.find((file) => file.status === 'success')
      if (value.length > 0 && atLeastOneUploadSucceeded) {
        callback(true, '')
      } else {
        callback(false, 'Upload is required')
      }
    }
  },
  uploadImmediatelyRequired: (value, field, props = {}, callback) => {
    if (!Array.isArray(value)) {
      callback(false, 'Upload is required')
    } else {
      const atLeastOneUploadSucceeded = value.find((file) => file.status === 'success')
      if (value.length > 0 && atLeastOneUploadSucceeded) {
        callback(true, '')
      } else {
        callback(false, 'Upload is required')
      }
    }
  },
  allSignaturesRequired: (value, field, props = {}, callback) => {
    const fields = store.getState().wizard.wizard.fields
    const printMailRequired = store.getState().wizard.requirePrintMailMethod.size > 0
    const srcRepeater = fields[field.srcRepeater] ? fields[field.srcRepeater] : null
    if (!printMailRequired) {
      if (field.multiple) {
        if (srcRepeater) {
          if (srcRepeater.value.length === value.length) {
            const allSignaturesFilled =
              value.length > 0
                ? value.every(
                    (signature) =>
                      valueIsNotEmptyOrNull(signature.sig) &&
                      valueIsNotEmptyOrNull(signature.date) &&
                      (field.showCapacity ? valueIsNotEmptyOrNull(signature.capacity) : true)
                  )
                : true
            if (allSignaturesFilled) {
              callback(true, '')
            } else {
              callback(false, 'All signatures are required.')
            }
          } else {
            callback(false, 'All signatures are required.')
          }
        } else {
          callback(false, '')
        }
      } else {
        if (value.length === 0) {
          callback(false, 'All signature fields are required.')
        } else {
          if (
            valueIsNotEmptyOrNull(value[0].sig) &&
            valueIsNotEmptyOrNull(value[0].date) &&
            (field.showCapacity
              ? valueIsNotEmptyOrNull(value[0].capacity) &&
                (value[0].capacity !== 'self' ? valueIsNotEmptyOrNull(value[0].behalf) : true)
              : true)
          ) {
            callback(true, '')
          } else {
            callback(false, 'All signature fields are required.')
          }
        }
      }
    } else {
      callback(null, '')
    }
  },
  minRequiredSignatures: (value, field, props = {}, callback) => {
    const fields = store.getState().wizard.wizard.fields
    const srcRepeater = fields[field.srcRepeater] ? fields[field.srcRepeater] : null
    var minRequiredSignatures = props.minRequiredSignatures
    var signatureCount = 0
    var signatureFieldsComplete = true
    if (field.multiple) {
      if (srcRepeater) {
        if (srcRepeater.value.length === value.length && value.length > 0) {
          value.forEach((signature) => {
            if (
              valueIsNotEmptyOrNull(signature.sig) &&
              valueIsNotEmptyOrNull(signature.date) &&
              (field.showCapacity ? valueIsNotEmptyOrNull(signature.capacity) : true)
            ) {
              signatureCount++
              signatureFieldsComplete = true
            } else {
              signatureFieldsComplete = false
            }
          })

          if (signatureCount >= minRequiredSignatures) {
            callback(true, '')
          } else if (!signatureFieldsComplete) {
            callback(false, 'All signature fields are required.')
          } else {
            callback(
              false,
              `At least ${minRequiredSignatures} signature${
                minRequiredSignatures > 1 ? 's' : ''
              } required.`
            )
          }
        } else {
          callback(
            false,
            `At least ${minRequiredSignatures} signature${
              minRequiredSignatures > 1 ? 's' : ''
            } required`
          )
        }
      } else {
        callback(false, 'This repeater has been configured incorrectly')
      }
    } else {
      if (value.length === 0) {
        callback(false, 'All signature fields are required.')
      } else {
        if (
          valueIsNotEmptyOrNull(value[0].sig) &&
          valueIsNotEmptyOrNull(value[0].date) &&
          (field.showCapacity
            ? valueIsNotEmptyOrNull(value[0].capacity) &&
              (value[0].capacity !== 'self' ? valueIsNotEmptyOrNull(value[0].behalf) : true)
            : true)
        ) {
          callback(true, '')
        } else {
          callback(false, 'All signature fields are required.')
        }
      }
    }
  },
  signaturesMinLength: (value, field, props = {}, callback) => {
    var isSignatureMinLength = true
    
    if (field.multiple) {
      value.forEach((signature) => {
        if (signature.sig.length < props.signaturesMinLength && valueIsNotEmptyOrNull(signature.sig) || signature.behalf.length < props.signaturesMinLength && valueIsNotEmptyOrNull(signature.behalf)) {
              isSignatureMinLength = false
            }
          })

          if (isSignatureMinLength === false){
            callback(
                false,
                props.errorMessage !== '' ? props.errorMessage : 'Signatures do not meet the minimum length requirement.'
            )
          } else {
            callback(true, '')
        }
    }
    else {
      if (valueIsNotEmptyOrNull(value[0].sig) || valueIsNotEmptyOrNull(value[0].behalf)) {
        if (value[0].sig.length < props.signaturesMinLength && valueIsNotEmptyOrNull(value[0].sig) || value[0].behalf.length < props.signaturesMinLength && valueIsNotEmptyOrNull(value[0].behalf)) {
          callback(
              false,
              props.errorMessage !== '' ? props.errorMessage : 'Signatures do not meet the minimum length requirement.'
          )
          } else {
            callback(true, '')
          }
        } else {
          callback(null, '')
        }
      }
    },
  formationLocaleRequired: (value, field, props = {}, callback) => {
    if (valueIsNotEmptyOrNull(value.FORMATION_TYPE)) {
      if (valueIsNotEmptyOrNull(value[value.FORMATION_TYPE])) {
        callback(true, '')
      } else {
        callback(false, props.errorMessage || 'Formation locale is required')
      }
    } else {
      callback(false, props.errorMessage || 'Formation locale is required')
    }
  },
  validateEntityNameFields: (value, field, props = {}, callback, { event, subField }) => {
    const onFirstBlurRules = ['entityNameNotTaken', 'entityIsValidName', 'entityNameRequired', 'customBusinessRules']
    const onSecondBlurRules = [
      'entityNameNotTaken',
      'entityValuesMatch',
      'entityNameRequired',
      'entityIsValidName',
      'customBusinessRules'
    ]
    const onBlurRules = [...onFirstBlurRules, 'entityValuesMatch']
    const rules = [
      ...Object.keys(props).filter(
        (ruleId) => ruleId !== 'filingTypeId' && ruleId !== 'filingSubtypeId'
      ),
      ...onBlurRules,
      'mustSelectReservedOption',
      'mustSelectAdditionalOption',
      'mustSelectConsentOption',
    ]
    let executableRules = rules
    if (event === 'onBlur') {
      if (subField === 'first') executableRules = onFirstBlurRules
      if (subField === 'second') executableRules = onSecondBlurRules
    }
    let removableRules = [];
    if (value.additionalYN === 'Y') {
      removableRules.push('entityIsValidName')
      removableRules.push('entityNameNotTaken')
    }
    if(props.disableNameNotTakenCheck){
      removableRules.push('entityNameNotTaken')
    }
    executableRules = executableRules.filter((ruleId) => !removableRules.includes(ruleId))
    
    let customRules = undefined;
    
    let fieldRules = field.businessRules.find( rule => rule.fn === 'customBusinessRules');
     
    // if fields are populated
    if(fieldRules && fieldRules.props && fieldRules.props.rules)
    {
       customRules = fieldRules.props.rules;
    }
     
    
    const rulePromises = executableRules.map(
      (ruleId) =>
        new Promise((resolve) => {
          getIsValid(
            ruleId,
            value,
            field,
            { filingTypeId: props.filingTypeId, filingSubtypeId: props.filingSubtypeId, rules: customRules },
            (isValid, errorMessage, payload) => {
              resolve({ ruleId, isValid, errorMessage, payload })
            }
          )
        })
    )
    Promise.all(rulePromises).then((values) => {
      const allValid = !values.some((result) => result.isValid === false)
      let errors = values.reduce((obj, current) => {
        return {
          ...obj,
          [current.ruleId]: { isValid: current.isValid, errorMessage: [current.errorMessage] },
        }
      }, {})
      let payloads = values.reduce((obj, current = {}) => {
        return { ...obj, ...current.payload }
      }, {})
      if (event === 'onBlur' && subField === 'first' && allValid) {
        payloads.nameMatch = null
      }
      payloads.setValue = false
      callback(allValid, errors, payloads)
    })
  },
  mustSelectReservedOption: (value, field, props = {}, callback) => {
    if (value.reservedYN === 'Y' || value.reservedYN === 'N' || field.hideReserved) {
      callback(true, '')
    } else {
      callback(false, 'This field is required.')
    }
  },
  mustSelectReservedName: (value, field, props = {}, callback) => {
    let resNameCheckOuter = [];
    api.GET_WEB_USER_RECORD({
      searchType: 'RESERVED_NAME',
      metadata: store.getState().wizard.wizard.meta,
      correctionMode: store.getState().wizard.correctionMode
    }, (resNameCheck) => {
      if (callback) {
        resNameCheckOuter = (resNameCheck && Array.isArray(resNameCheck) ? resNameCheck : []).map(
            name => ({
              value: name.SOURCE_ID,
              label: `${name.NAME}`,
              num: name.SOURCE_NUM,
              name: name.NAME
            })
        );
      }
      if (value.reservedYN === 'Y') {
        if (valueIsNotEmptyOrNull(value.reservedNameId) && (resNameCheckOuter.length > 0 && resNameCheckOuter.some(item => item.value === Number(value.reservedNameId)))) {
          callback(true, '')
        } else {
          callback(false, 'Please select a reserved name with which you wish to file.')
        }
      } else {
        callback(null, '')
      }
    })
  },
  entityNameRequired: (value, field, props = {}, callback) => {
    if (
      value.reservedYN === 'N' ||
      field.hideReserved ||
      (field.allowReservedNameEditing && value.reservedYN === 'Y')
    ) {
      if (valueIsNotEmptyOrNull(value.val1)) {
        callback(true, '')
      } else {
        callback(false, 'Please provide the entity name to continue.')
      }
    } else {
      callback(null, '')
    }
  },
  mustSelectAdditionalOption: (value, field, props = {}, callback) => {
    if ((value.reservedYN === 'N' || field.hideReserved) && !field.hideAdditionalName) {
      if (value.additionalYN === 'Y' || value.additionalYN === 'N') {
        callback(true, '')
      } else {
        callback(false, 'This field is required.')
      }
    } else {
      callback(null, '')
    }
  },
  mustSelectAdditionalName: (value, field, props = {}, callback) => {
    if ((value.reservedYN === 'N' || field.hideReserved) && value.additionalYN == 'Y') {
      if (valueIsNotEmptyOrNull(value.additionalNameId)) {
        callback(true, '')
      } else {
        callback(false, 'Please select a name to continue.')
      }
    } else {
      callback(null, '')
    }
  },
  mustSelectConsentOption: (value, field, props = {}, callback) => {
    if (
      !field.hideConsent &&
      (value.reservedYN === 'N' || field.hideReserved) &&
      (value.additionalYN === 'N' || field.hideAdditionalName)
    ) {
      if (value.consentYN === 'Y' || value.consentYN === 'N') {
        callback(true, '')
      } else {
        callback(false, 'This field is required.')
      }
    } else {
      callback(null, '')
    }
  },
  numConsentsRequired: (value, field, props = {}, callback) => {
    if (
      (value.reservedYN === 'N' || field.hideReserved) &&
      (value.additionalYN === 'N' || field.hideAdditionalName) &&
      value.consentYN === 'Y' &&
      !field.hideNumConsent
    ) {
      if (valueIsNotEmptyOrNull(value.numConsents) && valueIsANumber(value.numConsents)) {
        if (value.numConsents <= 100) {
          callback(true, '')
        } else {
          callback(false, 'The number of consents cannot exceed 100.')
        }
      } else {
        callback(false, 'Please provide the number of consents to continue.')
      }
    } else {
      callback(null, '')
    }
  },
  entityValuesMatch: (value, field, props = {}, callback) => {
    if (value.reservedYN === 'N' || field.hideReserved) {
      VALIDATE.valuesMatch(value, field, props, (isValid, errorMessage) => {
        callback(isValid, errorMessage, { nameMatch: isValid })
      })
    } else {
      callback(null, '')
    }
  },
  entityNameNotTaken: (value, field, props = {}, callback) => {
    if (
      (value.reservedYN === 'N' || field.hideReserved || field.allowReservedNameEditing) &&
      value.consentYN !== 'Y'
    ) {
      VALIDATE.nameNotTaken(value, field, props, (isValid, errorMessage) => {
        callback(isValid, errorMessage)
      })
    } else {
      callback(null, '')
    }
  },
  enitityHasNameEnding: (value, field, props = {}, callback) => {
    if (value.reservedYN === 'N' || field.hideReserved) {
      VALIDATE.hasNameEnding(value, field, props, (isValid, errorMessage) => {
        callback(isValid, errorMessage)
      })
    } else {
      callback(null, '')
    }
  },
  entityNoRegulatedWords: (value, field, props = {}, callback) => {
    if (value.reservedYN === 'N' || field.hideReserved) {
      VALIDATE.noRegulatedWords(value, field, props, (isValid, errorMessage) => {
        callback(isValid, errorMessage)
      })
    } else {
      callback(null, '')
    }
  },
  entityIsValidName: (value, field, props = {}, callback) => {
    if (
      value.reservedYN === 'N' ||
      field.hideReserved ||
      (field.allowReservedNameEditing && value.reservedYN === 'Y')
    ) {
      VALIDATE.isValidName(value, field, props, (isValid, errorMessage, payload) => {
        callback(isValid, errorMessage, payload)
      })
    } else {
      callback(null, '')
    }
  },
  mailingPoBoxNotAllowed: (value, field, props = {}, callback) => {
    VALIDATE.poBoxNotAllowed(value.MAIL_ADDR, field, props, callback)
  },
  poBoxNotAllowed: (value, field, props = {}, callback) => {
    const poBoxRegex = /\b(?:p\.?\s*o\.?|post\s+office)(\s+)?(?:box|b|[0-9]*)?\b.*/i
    const pmbRegex = /\b(?:p(?:ersonal)?\.?\s*[m](?:ail)?\s*b(?:[o]x)?).*/i //Personal Mail Box pattern
    if (emptyExceptDefaultState(value, field)) {
      callback(null, '')
    } else if (
      poBoxRegex.test(value.ADDR1) ||
      poBoxRegex.test(value.ADDR2) ||
      poBoxRegex.test(value.ADDR3) ||
      pmbRegex.test(value.ADDR1) ||
      pmbRegex.test(value.ADDR2) ||
      pmbRegex.test(value.ADDR3) ||
      value.ADDR1.toLowerCase().startsWith('box ') ||
      value.ADDR2.toLowerCase().startsWith('box ') ||
      value.ADDR3.toLowerCase().startsWith('box ')
    ) {
      callback(false, props.errorMessage || 'Address may not contain a PO Box')
    } else {
      callback(true, '')
    }
  },
  dateRangeRequired: (value, field, props = {}, callback) => {
    if (valueIsNotEmptyOrNull(value.start) && valueIsNotEmptyOrNull(value.end)) {
      callback(true, '')
    } else {
      callback(false, props.errorMessage || 'A date range is required.')
    }
  },
  minDate: (value, field, props = {}, callback) => {
    if (props.minDateOptions !== 'custom') {
      api.VALIDATE(
        'MinServerDate',
        {
          value: value,
          source: props.minDateOptions,
          metadata: store.getState().wizard.wizard.meta,
        },
        (valid) => {
          callback(valid.valid, valid.error)
        }
      )
    } else {
      if (isAfter(new Date(value), new Date(props.customMinDate))) {
        callback(true, '')
      } else {
        callback(false, props.errorMessage || `Please select a date after ${props.customMinDate}`)
      }
    }
  },
  maxDate: (value, field, props = {}, callback) => {
    if (props.maxDateOptions !== 'custom') {
      api.VALIDATE(
        'MaxServerDate',
        {
          value: value,
          source: props.maxDateOptions,
          metadata: store.getState().wizard.wizard.meta,
        },
        (valid) => {
          callback(valid.valid, valid.error)
        }
      )
    } else {
      if (isBefore(new Date(value), new Date(props.customMaxDate))) {
        callback(true, '')
      } else {
        callback(false, props.errorMessage || `Please select a date before ${props.customMaxDate}`)
      }
    }
  },
  minDateRange: (value, field, props = {}, callback) => {
    if (props.minDateOptions !== 'custom') {
      api.VALIDATE(
        'MinServerDate',
        {
          value: value.start,
          source: props.minDateOptions,
          metadata: store.getState().wizard.wizard.meta,
        },
        (valid) => {
          callback(valid.valid, valid.error)
        }
      )
    } else if (isAfter(new Date(value.start), new Date(props.customMinDate))) {
      callback(true, '')
    } else {
      callback(false, props.errorMessage || `Please select a date after ${props.customMinDate}`)
    }
  },
  maxDateRange: (value, field, props = {}, callback) => {
    if (props.minDateOptions !== 'custom') {
      api.VALIDATE(
        'MaxServerDate',
        {
          value: value.end,
          source: props.minDateOptions,
          metadata: store.getState().wizard.wizard.meta,
        },
        (valid) => {
          callback(valid.valid, valid.error)
        }
      )
    } else if (isBefore(new Date(value.end), new Date(props.customMaxDate))) {
      callback(true, '')
    } else {
      callback(false, props.errorMessage || `Please select a date before ${props.customMaxDate}`)
    }
  },
  repeaterColumnsMustBeUnique: (value, field, props = {}, callback) => {
    const constraints = props.constraints
    const rows = field.value.filter((row) => row._statusId !== 3)
    const fields = store.getState().wizard.wizard.fields
    const results = testRepeaterConstraints(constraints, rows, fields)
    const errors = results.reduce((errs, result, i) => {
      if (result === false) {
        const constraint = constraints[i]
        const constFields = constraint.fields
          .map((fieldId) =>
            fields[fieldId] ? fields[fieldId].shortLabel || fields[fieldId].label : null
          )
          .filter((x) => x)
        return [
          ...errs,
          constraint.errorMessage ||
            `At least one of the following fields must be unique: ${constFields.join(', ')}. \n`,
        ]
      }
      return errs
    }, [])
    const isValid = results.every((t) => t === true)
    callback(isValid, errors)
  },
  isValidEmailFormat: (value, field, props = {}, callback) => {
    // base regex retrieved from https://regexr.com/2rhq7
    const regex = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?!.*@)(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/gi
    const sanitizedValue = ((field.type === 'confirmFields' ? value.val1 : value) || '').trim()
    if (valueIsNotEmptyOrNull(sanitizedValue)) {
      const isValid = regex.test(sanitizedValue) && !sanitizedValue.includes(' ')
      if (isValid) {
        callback(true, '')
      } else {
        callback(false, props.errorMessage || 'Please enter a properly formatted email address.')
      }
    } else {
      callback(null, '')
    }
  },
  timeRequired: (value, field, props = {}, callback) => {
    if (value.length >= 7) {
      callback(true, '')
    } else {
      callback(false, props.errorMessage || 'This field is required.')
    }
  },
  uccSearchRequired: (value, field, props = {}, callback) => {
    const UCC4Satisfied =
      field.uccMode.toString() === '4'
        ? value.search.SEARCH_UCC ||
          value.search.SEARCH_GOV ||
          value.search.SEARCH_EFS ||
          value.search.SEARCH_CROP_LIEN ||
          value.search.SEARCH_AG_LIEN
        : true
    const commonFieldCheck =
      (value.search.IS_ORG === true || value.search.IS_ORG === 'true'
        ? value.search.SEARCH_ORG_NAME
        : process.env.UCC_SEARCH_ONLY_REQUIRE_LAST_NAME
        ? value.search.SEARCH_LAST_NAME
        : value.search.SEARCH_FIRST_NAME && value.search.SEARCH_LAST_NAME) && UCC4Satisfied
    const checkCommonFields = value => {
      if (value.cache && commonFieldCheck) {
        callback(true, '')
      } else {
        let errorMessage = []
        if (!value.cache) {
          errorMessage.push('You must execute a search.')
        }
        if (value.search.IS_ORG === true || value.search.IS_ORG === 'true') {
          if (!value.search.SEARCH_ORG_NAME) {
            errorMessage.push('Organization name is required.')
          }
        } else {
          if (
            process.env.UCC_SEARCH_ONLY_REQUIRE_LAST_NAME
              ? !value.search.SEARCH_LAST_NAME
              : !value.search.SEARCH_FIRST_NAME || !value.search.SEARCH_LAST_NAME
          ) {
            errorMessage.push(
              process.env.UCC_SEARCH_ONLY_REQUIRE_LAST_NAME
                ? 'A last name must be entered'
                : 'First and last name are required.'
            )
          }
        }
        if (!UCC4Satisfied) {
          errorMessage.push('At least one of the UCC 4 search types must be selected.')
        }
        callback(false, errorMessage.join('\n'))
      }
    }
    if (process.env.UCC_SEARCH_ALLOW_SEARCH_TYPE) {
      if (
        value.cache &&
        (value.search.SEARCH_TYPE === 'UCC_NUM' ? value.SEARCH_UCC_NUM !== '' : commonFieldCheck)
      ) {
        callback(true, '')
      } else {
        let errorMessage = []
        if (value.search.SEARCH_TYPE === 'UCC_NUM') {
          if (!value.cache) {
            errorMessage.push('You must execute a search.')
          }
          if (!value.search.SEARCH_UCC_NUM) {
            errorMessage.push('File number is required.')
          }
          callback(false, errorMessage.join('\n'))
        } else {
          checkCommonFields(value)
        }
      }
    } else {
      checkCommonFields(value)
    }
  },
  atLeastOneUccSearchResultIsRequired: (value, field, props = {}, callback) => {
    const isPartial = value.IS_PARTIAL
    const lengthIsSatisfied = value.cache && value.cache.rows.length > 0
    const defaultErrorMessage = 'Search must return at least one result.'
    const checkLength = () => {
      if(props.onlyRequireWhenExists){
        if(!lengthIsSatisfied && isPartial){
          callback(false, props.errorMessage || defaultErrorMessage)
        }
        else{
          callback(true, '')
        }
      }
      else{
        if (lengthIsSatisfied) {
          callback(true, '')
        } else {
          callback(false, props.errorMessage || defaultErrorMessage)
        }
      }
    }
    if (props.onlyAppliesToFileNumber) {
      if (value.search.SEARCH_TYPE !== 'UCC_NUM') {
        callback(null, '')
      } else {
        checkLength()
      }
    } else {
      checkLength()
    }
  },
  mustSelectLocalSelect: (value, field, props = {}, callback) => {
    const fields = store.getState().wizard.wizard.fields
    const srcRepeater = fields[field.srcRepeater]
    const srcField = fields[field.srcField]
    if (srcRepeater && srcField) {
      if (valueIsNotEmptyOrNull(value)) {
        const valueExists = srcRepeater.value.find((row) => row._rowId === value)
        if (valueExists) {
          callback(true, '')
        } else {
          callback(false, props.errorMessage || 'Select an option to continue.')
        }
      } else {
        callback(false, props.errorMessage || 'Select an option to continue.')
      }
    } else {
      callback(false, 'You must assign a Source Repeater and a Source Field to use this control.')
    }
  },
  enforceRepeaterChildConstraints: (value, field, props = {}, callback) => {
    if (value.length > 0) {
      const fields = store.getState().wizard.wizard.fields
      const columns = field.template.map((fieldId) => fields[fieldId])
      validateRepeaterConstraints(columns, field).then((resolutions) => {
        const isValids = resolutions.map((r) => r.isValid)
        const errors = resolutions.map((r) => r.errorMessage).join('\n')
        if (isValids.some((v) => v === false)) {
          callback(false, errors)
        } else if (isValids.some((v) => v === true)) {
          callback(true, '')
        } else {
          callback(null, '')
        }
      })
    } else {
      callback(null)
    }
  },
  minMultiComplete: (value, field, props = {}, callback) => {
    if (value.length >= props.min) {
      callback(true, '')
    } else {
      callback(false, props.errorMessage || `You must select at least ${props.min} to continue.`)
    }
  },
  efsRequired: (value, field, props = {}, callback) => {
    if (value.yearType) {
      switch (value.yearType) {
        case 'duration':
          if (value.startYear && value.endYear) callback(true, '')
          else
            callback(
              false,
              props.durationErrorMessage || 'Select a start and end year to continue.'
            )
          break
        case 'arbitrary':
          if (value.years.length > 0) callback(true, '')
          else
            callback(false, props.arbitraryErrorMessage || 'Select at least one year to continue.')
          break
        case 'all':
        case 'none':
        default:
          callback(true, '')
      }
    } else {
      callback(false, props.errorMessage || 'Select a crop year to continue.')
    }
  },
  mayOnlyAddAdditionalRows: (value, field, props = {}, callback) => {
    const original = store.getState().wizard.diff.originalByFieldId[field.fieldId]
    const prevNumRows = original ? original.length : 0
    const numRows = value.length
    if (numRows <= prevNumRows + props.numRows) {
      callback(true, '')
    } else {
      callback(
        false,
        props.errorMessage || `You may not add more than ${prevNumRows + props.numRows} row(s).`
      )
    }
  },
  mustHaveSameNumRowsAs: (value, field, props = {}, callback) => {
    const { repeater } = props
    if (repeater) {
      const otherRepeater = store.getState().wizard.wizard.fields[repeater]
      if (otherRepeater) {
        if (otherRepeater.value.length === field.value.length) {
          callback(true, '')
        } else {
          callback(
            false,
            props.errorMessage || `You must provide exactly ${otherRepeater.value.length} row(s).`
          )
        }
      } else {
        callback(null, '')
      }
    } else {
      callback(false, 'This business rule failed because a repeater was not selected.')
    }
  },
  onlyNumbers: (value, field, props = {}, callback) => {
    if (!valueIsNotEmptyOrNull(value)) {
      callback(null, '')
    } else if (props.allowCommas ? !(/^((\d+|\d{1,3}(,\d{3})*)(\.\d+)?|\.\d+)$/.test(value)) : /\D+/g.test(value)) {
      callback(false, props.errorMessage || 'This field must only contain numbers.')
    } else {
      callback(true, '')
    }
  },
  maxCharacters: (value, field, props = {}, callback) => {
    if (valueIsNotEmptyOrNull(value)) {
      if (value.length > props.maxCharacters) {
        callback(
          false,
          props.errorMessage ||
            `This field may not contain more than ${props.maxCharacters} character(s).`
        )
      } else {
        callback(true)
      }
    } else {
      callback(null)
    }
  },
  minCharacters: (value, field, props = {}, callback) => {
    if (valueIsNotEmptyOrNull(value)) {
      if (value.length < props.minCharacters) {
        callback(
          false,
          props.errorMessage ||
            `This field must contain at least ${props.minCharacters} character(s).`
        )
      } else {
        callback(true)
      }
    } else {
      callback(null)
    }
  },
  minRepeaterEdit: (value, field, props = {}, callback) => {
    const edited = value.filter((r) => r._statusId === 2)
    if (edited.length < props.minRows) {
      callback(false, `You must edit at least ${props.minRows} row(s) to continue.`)
    } else {
      callback(true, '')
    }
  },
  minRepeaterDelete: (value, field, props = {}, callback) => {
    const deleted = value.filter((r) => r._statusId === 3)
    if ((props.disableIfOne ? value.length >= 1 : true) && deleted.length < props.minRows) {
      callback(
        false,
        props.errorMessage || `You must delete at least ${props.minRows} row(s) to continue.`
      )
    } else {
      callback(true, '')
    }
  },
  checkForRegulatedWords: (value, field, props = {}, callback) => {
    const promises = props.regulatedWordLists.map(category => (
      new Promise((resolve, reject) => {
        api.VALIDATE("TriggerWords", {
          CATEGORY: category,
          VALUE: value,
          FIELD_TYPE: field.type
        },
        response => resolve(response),
        err => reject(err)
        )
      })
    ))
    
    Promise.all(promises).then(responses => {
      const isValid = responses.every(res => res.valid !== false)
      const errorMessage = responses.filter(res => res.error).map(res => res.error).join("\n")
      const infoMessage = responses.filter(res => res.payload.INFO_MESSAGE).map(res=>res.payload.INFO_MESSAGE).join("\n")
      const triggerInternalReview = responses.some(res => res.payload.TRIGGER_INTERNAL_REVIEW)
      if(triggerInternalReview){
        store.dispatch({
          type: 'ADD_INTERNAL_REVIEW_REQUIRED',
          fieldId: field.fieldId
        })
      }else{
        store.dispatch({
          type: 'REMOVE_INTERNAL_REVIEW_REQUIRED',
          fieldId: field.fieldId
        })
      }
      callback(isValid, errorMessage, { infoMessage })
    })
  },
  customBusinessRules: (value, field, props = {}, callback, options = {}) => {
    if(props.rules && props.rules.length){
      const ruleResults = props.rules.map(rule => (
        resolveFieldEngine.resolveRootNode(rule.logicNodes, { context: options })
      ))
      callback(
        ruleResults.every(res => res.isValid),
        ruleResults
          .filter(res => res.isValid === false)
          .map(res => res.errorMessage)
          .join("\n")
      )
    }else{
      callback(null, '')
    }
  }
}

const getIsValid = (fn, value, field, props, callback, options) => {
  const validateFunction = VALIDATE[fn];
  if(validateFunction){
    VALIDATE[fn](
      value,
      field,
      props,
      (valid, errorMessage, payload) => {
        callback(valid, errorMessage, payload)
      },
      options
    )
  }else{
    callback(null, "")
  }
}

export const loopThroughRules = ({ businessRules, value, field, event, options }, callback) => {
  let validation = {
    valids: [],
    errors: [],
    payload: {},
  }
  var promises = []
  for (var i = 0; i < businessRules.length; i++) {
    var rule = businessRules[i]
    promises[i] = new Promise((resolve, reject) => {
      if (BUSINESS_RULES[rule.fn][event] === true) {
        getIsValid(
          rule.fn,
          value,
          field,
          rule.props,
          (valid, errorMessage, payload) => {
            if (valid === true || valid === false) {
              validation.valids.push(valid)
            }
            validation.errors.push(errorMessage)
            validation.payload = { ...validation.payload, ...payload }
            resolve('Returned')
          },
          { event, ...options }
        )
      } else {
        resolve()
      }
    })
  }

  Promise.all(promises).then((values) => {
    callback(validation)
  })
}

export const MASK = {
  onlyNumbers: (value, props = {}) => {
    //Tests that the value contains only numbers, and returns the value minus letters if not
    if (value === '') {
      return value
    } else {
      let sanitizedValue = value
      if (props.allowDecimal && value.includes('.')) {
        let segments = value.split('.')
        if (props.commas) {
          segments[0] = segments[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
          sanitizedValue = `${segments[0]}.${segments.slice(1).join('')}`
        } else {
          segments = segments.map((seg) => seg.replace(/\D+/g, ''))
          sanitizedValue = `${segments[0].replace(/\D+/g, '')}.${segments.slice(1).join('')}`
        }
      } else {
        sanitizedValue = value.replace(/\D+/g, '')
        if (props.leadingZerosAllowed === false) {
          sanitizedValue = parseInt(sanitizedValue === '' ? 0 : sanitizedValue, 10).toString()
        }
        if (props.commas) {
          sanitizedValue = sanitizedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        }
      }
      return sanitizedValue
    }
  },
  phone: (value, props) => {
    let x = value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/)
    value = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '')
    return value
  },
  social: (value, props) => {
    var val = value.replace(/\D/g, '')
    var newVal = ''
    if (val.length > 4) {
      value = val
    }
    if (val.length > 3 && val.length < 6) {
      newVal += val.substr(0, 3) + '-'
      val = val.substr(3)
    }
    if (val.length > 5) {
      newVal += val.substr(0, 3) + '-'
      newVal += val.substr(3, 2) + '-'
      val = val.substr(5)
    }
    newVal += val
    value = newVal.substring(0, 11)
    return value
  },
  fein: (value, props = {}) => {
    let val = value.replace(/\D+/g, '').split('')
    let newVal
    if (val.length > 2) {
      newVal = val.slice(0, 2).join('') + '-' + val.slice(2, 9).join('')
    } else {
      newVal = val.join('')
    }
    return newVal
  },
  currency: (value, props = {}) => {
    let hasDecimal = value.split('.').length > 1
    if (value === '$' || value === '$ ' || value === ' ') {
      return ''
    }
    if (hasDecimal) {
      let segments = value.split('.')
      let extraSegements = segments.slice(2)
      let seg1 = segments[0]
        .replace(/\D/g, '')
        .replace(/$ /g, '')
        .replace(/$/g, '')
        .replace(/ /g, '')
      let seg2 = segments[1]
      if (extraSegements) {
        seg2 += extraSegements.join()
      }
      seg2 = seg2.replace(/\D/g, '').replace(/$ /g, '').replace(/$/g, '').replace(/ /g, '')
      if (props.commas) {
        seg1 = seg1.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      }
      return `$${seg1}.${seg2}`
    } else {
      let sanitized = value
        .replace(/\D/g, '')
        .replace(/$ /g, '')
        .replace(/$/g, '')
        .replace(/ /g, '')
      if (props.commas) {
        sanitized = sanitized.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      }
      return `$${sanitized}`
    }
  },
  // onlyLetters: (value, field, props = {}) => {
  onlyLetters: (value, props = {}) => {
    if (Array.isArray(value)){
      return value.map(v => MASK.onlyLettersSignature(v, props))
    } else if (value.sig !== undefined){
      return MASK.onlyLettersSignature(value, props)
    }
    let valueToReturn = ''
    let originalValue = value.split("")
    originalValue.forEach(removeProhibitedCharacters)
    function removeProhibitedCharacters (item) {
      let trimmedAllowedSpecialCharacters = props.allowedSpecialCharacters.replace(/\s/g,'')
      if (trimmedAllowedSpecialCharacters.includes(item)) {
        valueToReturn = valueToReturn + item
      }
      else {
        valueToReturn = valueToReturn + item.replace(/[^a-zA-Z\s]/g, '')
      }
    }
    return value.replace(value, valueToReturn)
    
    // if (props.allowedSpecialCharacters.trim.includes(value)){
    //   return value
    // }
    // else{
    //   return value.replace(/[^a-zA-Z\s]/g, '')
    // }
  },
  onlyLettersSignature: (value, props) => {
    let newVal = {...value}
    newVal.sig = MASK.onlyLetters(newVal.sig || "", props)
    newVal.behalf = MASK.onlyLetters(newVal.behalf || "", props)
    return newVal
  },
  maxStringLength: (value, props = {}) => {
    return value.slice(0, props.max)
  },
  zipCode: (value, props = {}) => {
    let val = value.replace(/\D+/g, '').split('')
    if (val.length > 5) {
      val = val.slice(0, 5).join('') + '-' + val.slice(5, 9).join('')
    } else {
      val = val.join('')
    }
    return val
  },
  addressZipCode: (value, props = {}) => {
    let newVal = { ...value }
    newVal.POSTAL_CODE = MASK.zipCode(newVal.POSTAL_CODE)
    return newVal
  },
  onlyAscii: (value, props = {}) => {
    if (typeof value.val1 === 'string' || typeof value.val2 === 'string') {
      return MASK.onlyAsciiConfirmFields(value, props)
    } else {
      return value.replace(/[^\x20-\x7e]/g, '')
    }
  },
  onlyAsciiConfirmFields: (value, props = {}) => {
    return {
      ...value,
      val1: MASK.onlyAscii(value.val1, props),
      val2: MASK.onlyAscii(value.val2, props),
    }
  },
  allUpperCase: (value = '', props = {}) => {
    return (value === null ? '' : value).toUpperCase()
  },
  addressUpperCase: (value, props = {}) => {
    let newVal = { ...value }
    newVal.ADDR1 = MASK.allUpperCase(newVal.ADDR1)
    newVal.ADDR2 = MASK.allUpperCase(newVal.ADDR2)
    newVal.ADDR3 = MASK.allUpperCase(newVal.ADDR3)
    newVal.CITY = MASK.allUpperCase(newVal.CITY)
    newVal.STATE = MASK.allUpperCase(newVal.STATE)
    newVal.POSTAL_CODE = MASK.allUpperCase(newVal.POSTAL_CODE)
    if (newVal.MAIL_ADDR) {
      newVal.MAIL_ADDR.ADDR1 = MASK.allUpperCase(newVal.MAIL_ADDR.ADDR1)
      newVal.MAIL_ADDR.ADDR2 = MASK.allUpperCase(newVal.MAIL_ADDR.ADDR2)
      newVal.MAIL_ADDR.ADDR3 = MASK.allUpperCase(newVal.MAIL_ADDR.ADDR3)
      newVal.MAIL_ADDR.CITY = MASK.allUpperCase(newVal.MAIL_ADDR.CITY)
      newVal.MAIL_ADDR.STATE = MASK.allUpperCase(newVal.MAIL_ADDR.STATE)
      newVal.MAIL_ADDR.POSTAL_CODE = MASK.allUpperCase(newVal.MAIL_ADDR.POSTAL_CODE)
    }
    return newVal
  },
  time: (value, props = {}) => {
    const splitValue = value.split(' ')
    const time = splitValue[0].replace(':', '')
    const period = splitValue[1]
    // const masked = time.replace(/^(0?[1-9]|1[012]):[0-5][0-9]$/g, '')
    let masked = ''
    if (time[0] == '0' || time[0] == '1') {
      masked += time[0]
    } else {
      masked += ''
    }
    if (time[0] == '0') {
      masked += ['1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(time[1]) ? time[1] : ''
    }
    if (time[0] == '1') {
      masked += ['1', '2'].includes(time[1]) ? time[1] : ''
    }
    if (masked.length === 2) {
      masked += ['0', '1', '2', '3', '4', '5'].includes(time[2]) ? ':' + time[2] : ''
    }
    if (masked.length === 4) {
      masked += ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].includes(time[3]) ? time[3] : ''
    }

    return props.justTime ? masked : `${masked} ${period}`
  },
}

export const maskValue = (value, mask) => {
  for (var i = 0; i < mask.length; i++) {
    value = MASK[mask[i].fn](value, mask[i].props)
  }
  return value
}



// WEBPACK FOOTER //
// ./src/components/Wizard2/validate.js